// src/GuestSearch.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import "./guest-search.css";
import { API } from "./apiBase"; // <- use the centralized resolver

function Field({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-2 text-sm sm:text-base">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-100 truncate max-w-[60%] sm:max-w-[70%]">
        {value || "-"}
      </span>
    </div>
  );
}

function Pill({ children, tone = "default" }) {
  const base =
    "inline-flex items-center gap-2 px-2.5 py-1 rounded-full border text-xs sm:text-sm select-none";
  const tones = {
    default: "bg-slate-800/60 border-slate-700 text-slate-200",
    good: "bg-emerald-900/40 border-emerald-700 text-emerald-200",
    warn: "bg-amber-900/40 border-amber-700 text-amber-200",
    bad: "bg-rose-900/40 border-rose-700 text-rose-200",
  };
  return <span className={`${base} ${tones[tone]}`}>{children}</span>;
}

const isImage = (name = "", mime = "") =>
  /\.(png|jpe?g|webp|gif)$/i.test(name) || /^image\//i.test(mime);
const isPDF = (name = "", mime = "") =>
  /\.pdf$/i.test(name) || /^application\/pdf$/i.test(mime);

/* ---------- vCard helpers ---------- */
const escapeVCF = (s = "") =>
  String(s)
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");

const buildVCard = (c = {}) => {
  const lines = [];
  const FN = escapeVCF(c.name || "");
  const [first = "", ...rest] = (c.name || "").trim().split(/\s+/);
  const last = rest.join(" ");
  lines.push("BEGIN:VCARD");
  lines.push("VERSION:3.0");
  lines.push(`N:${escapeVCF(last)};${escapeVCF(first)};;;`);
  lines.push(`FN:${FN}`);
  if (c.title) lines.push(`TITLE:${escapeVCF(c.title)}`);
  if (c.company) lines.push(`ORG:${escapeVCF(c.company)}`);
  if (c.email) lines.push(`EMAIL;TYPE=INTERNET:${escapeVCF(c.email)}`);
  if (c.phones) {
    const phones = String(c.phones)
      .split(/[,/|;]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    phones.forEach((p) => lines.push(`TEL;TYPE=CELL:${escapeVCF(p)}`));
  }
  if (c.address) lines.push(`ADR;TYPE=HOME:;;${escapeVCF(c.address)};;;;`);
  if (c.birthday) lines.push(`BDAY:${escapeVCF(c.birthday)}`);
  lines.push("END:VCARD");
  return lines.join("\r\n");
};

const downloadVCard = (c = {}) => {
  const blob = new Blob([buildVCard(c)], { type: "text/vcard;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const safeName = (c.name || "contact").replace(/[^\w\s.-]+/g, "").trim() || "contact";
  a.href = url;
  a.download = `${safeName}.vcf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/* ---------- URL helpers ---------- */
const joinURL = (base, path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const b = (base || "").replace(/\/+$/, "");
  const p = String(path).startsWith("/") ? path : "/" + path;
  return b + p;
};
const urlForView = (f) => {
  if (f?.view_url) return joinURL(API, f.view_url);
  if (f?.download_url) return joinURL(API, f.download_url);
  return "";
};
const urlForDownload = (f) => {
  if (f?.download_url) return joinURL(API, f.download_url);
  if (f?.view_url) {
    const sep = f.view_url.includes("?") ? "&" : "?";
    return joinURL(API, `${f.view_url}${sep}download=1`);
  }
  return "";
};

export default function GuestSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", phones: "", birthday: "" });

  const [voucherMap, setVoucherMap] = useState({});
  const [voucherLoading, setVoucherLoading] = useState({});
  const [voucherError, setVoucherError] = useState({});

  const [expanded, setExpanded] = useState({});
  const toggleExpanded = (idx) => setExpanded((m) => ({ ...m, [idx]: !m[idx] }));

  // Fullscreen viewer state
  const [viewer, setViewer] = useState({ open: false, idx: null, at: 0 });

  // Blob URL state for viewer
  const [viewerSrc, setViewerSrc] = useState("");
  const [viewerMime, setViewerMime] = useState("");
  const [viewerBusy, setViewerBusy] = useState(false);
  const [viewerErr, setViewerErr] = useState("");

  // ✅ DELETE additions: per-row delete state
  const [deleteBusy, setDeleteBusy] = useState({}); // { [idx]: true/false }

  const canSearch = useMemo(() => query.trim().length >= 1, [query]);

  const doSearch = async () => {
    if (!canSearch) return;
    setLoading(true);
    setErr("");
    try {
      const resp = await fetch(`${API}/contacts?search=${encodeURIComponent(query)}`, {
        method: "GET",
        mode: "cors",
        credentials: "omit",
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(`${resp.status} ${resp.statusText} — ${t}`);
      }
      const data = await resp.json();
      const items = data.contacts || [];
      setResults(items);
      setVoucherMap({});
      setVoucherLoading({});
      setVoucherError({});
      setExpanded({});
      setDeleteBusy({});
      closeViewer(); // reset viewer on new search
    } catch (e) {
      console.error(e);
      setErr("Search failed. Please check API/CORS.");
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") doSearch();
  };

  const startEdit = (idx) => {
    const row = results[idx];
    setEditForm({
      name: row.name || "",
      phones: row.phones || "",
      birthday: row.birthday || "",
    });
    setEditingIndex(idx);
  };
  const cancelEdit = () => setEditingIndex(null);
  const handleEditChange = (e) => setEditForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const saveEdit = async () => {
    const original = results[editingIndex];
    try {
      const resp = await fetch(`${API}/contacts`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identifier: { name: original.name, phones: original.phones },
          name: editForm.name,
          phones: editForm.phones,
          birthday: editForm.birthday,
        }),
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(`${resp.status} ${resp.statusText} — ${t}`);
      }
      const updated = [...results];
      updated[editingIndex] = { ...updated[editingIndex], ...editForm };
      setResults(updated);
      setEditingIndex(null);
    } catch (e) {
      console.error(e);
      alert("Failed to update contact");
    }
  };

  // ✅ DELETE additions: delete handler
  const deleteGuest = async (idx) => {
    const c = results[idx];
    if (!c) return;

    const name = c?.name || "";
    const phones = c?.phones || "";

    const ok = window.confirm(
      `Delete this guest?\n\nName: ${name || "-"}\nPhone: ${phones || "-"}\n\nThis cannot be undone.`
    );
    if (!ok) return;

    setDeleteBusy((m) => ({ ...m, [idx]: true }));

    try {
      const resp = await fetch(`${API}/contacts`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identifier: { name, phones },
        }),
      });

      // Many APIs return 204 No Content for deletes
      if (!resp.ok) {
        const t = await resp.text().catch(() => "");
        throw new Error(`${resp.status} ${resp.statusText}${t ? " — " + t : ""}`);
      }

      // If viewer is open and points to this idx, close it
      if (viewer.open && viewer.idx === idx) {
        closeViewer();
      }

      // Remove from UI + clean maps
      setResults((prev) => prev.filter((_, i) => i !== idx));
      setVoucherMap((m) => {
        const next = {};
        Object.keys(m).forEach((k) => {
          const i = Number(k);
          if (i < idx) next[i] = m[i];
          else if (i > idx) next[i - 1] = m[i];
        });
        return next;
      });
      setVoucherLoading((m) => {
        const next = {};
        Object.keys(m).forEach((k) => {
          const i = Number(k);
          if (i < idx) next[i] = m[i];
          else if (i > idx) next[i - 1] = m[i];
        });
        return next;
      });
      setVoucherError((m) => {
        const next = {};
        Object.keys(m).forEach((k) => {
          const i = Number(k);
          if (i < idx) next[i] = m[i];
          else if (i > idx) next[i - 1] = m[i];
        });
        return next;
      });
      setExpanded((m) => {
        const next = {};
        Object.keys(m).forEach((k) => {
          const i = Number(k);
          if (i < idx) next[i] = m[i];
          else if (i > idx) next[i - 1] = m[i];
        });
        return next;
      });
      setDeleteBusy((m) => {
        const next = {};
        Object.keys(m).forEach((k) => {
          const i = Number(k);
          if (i < idx) next[i] = m[i];
          else if (i > idx) next[i - 1] = m[i];
        });
        return next;
      });
    } catch (e) {
      console.error(e);
      alert("Failed to delete guest");
    } finally {
      setDeleteBusy((m) => ({ ...m, [idx]: false }));
    }
  };

  // Fetch vouchers for one card
  const fetchVouchersForIdx = async (idx) => {
    const c = results[idx];
    if (!c) return [];
    const phone = (c?.phones || "").toString();
    const name = c?.name || "";
    setVoucherLoading((m) => ({ ...m, [idx]: true }));
    setVoucherError((m) => ({ ...m, [idx]: "" }));
    try {
      const resp = await fetch(
        `${API}/vouchers/check?name=${encodeURIComponent(name)}&phone=${encodeURIComponent(phone)}`,
        { method: "GET", mode: "cors", credentials: "omit" }
      );
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(`${resp.status} ${resp.statusText} — ${t}`);
      }
      const data = await resp.json();
      const files = data.files || [];
      setVoucherMap((m) => ({ ...m, [idx]: files }));
      return files;
    } catch (e) {
      console.error(e);
      setVoucherError((m) => ({ ...m, [idx]: "Could not load vouchers." }));
      return [];
    } finally {
      setVoucherLoading((m) => ({ ...m, [idx]: false }));
    }
  };

  // Auto-fetch vouchers when results change
  useEffect(() => {
    if (!results || results.length === 0) return;
    results.forEach((_, idx) => {
      fetchVouchersForIdx(idx);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  const filesForIdx = (idx) => voucherMap[idx] || [];
  const vLoading = (idx) => !!voucherLoading[idx];
  const vError = (idx) => voucherError[idx] || "";
  const dLoading = (idx) => !!deleteBusy[idx];

  // Open viewer helpers
  const openViewer = (idx, at = 0) => setViewer({ open: true, idx, at });
  const closeViewer = () => {
    setViewer({ open: false, idx: null, at: 0 });
    if (viewerSrc) {
      URL.revokeObjectURL(viewerSrc);
      setViewerSrc("");
    }
    setViewerErr("");
  };

  // Ensure we have files then open viewer
  const handleOpenVouchers = async (idx, at = 0) => {
    let files = filesForIdx(idx);
    if (!files.length && !vLoading(idx)) {
      files = await fetchVouchersForIdx(idx);
    }
    if (files.length) openViewer(idx, Math.max(0, Math.min(at, files.length - 1)));
    else alert("No vouchers found for this guest.");
  };

  // Load current file as Blob -> objectURL
  useEffect(() => {
    if (!viewer.open || !Number.isInteger(viewer.idx)) return;
    const files = filesForIdx(viewer.idx);
    const f = files[viewer.at];
    if (!f) return;

    const href = urlForView(f) || urlForDownload(f);
    if (!href) return;

    // cleanup old URL
    if (viewerSrc) {
      URL.revokeObjectURL(viewerSrc);
      setViewerSrc("");
    }
    setViewerMime("");
    setViewerErr("");
    setViewerBusy(true);

    const ac = new AbortController();
    fetch(href, { mode: "cors", credentials: "omit", signal: ac.signal })
      .then(async (res) => {
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setViewerSrc(url);
        setViewerMime(blob.type || "");
      })
      .catch((e) => {
        console.error("Viewer fetch failed:", e);
        setViewerErr("Could not open this voucher.");
      })
      .finally(() => setViewerBusy(false));

    return () => {
      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer.open, viewer.idx, viewer.at, voucherMap]);

  // Keyboard nav inside viewer
  const handleViewerKey = useCallback(
    (e) => {
      if (!viewer.open) return;
      if (e.key === "Escape") {
        e.preventDefault();
        closeViewer();
        return;
      }
      if (!Number.isInteger(viewer.idx)) return;
      const files = filesForIdx(viewer.idx);
      if (!files.length) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        setViewer((v) => ({ ...v, at: (v.at + 1) % files.length }));
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setViewer((v) => ({ ...v, at: (v.at - 1 + files.length) % files.length }));
      }
    },
    [viewer.open, viewer.idx, voucherMap]
  );
  useEffect(() => {
    document.addEventListener("keydown", handleViewerKey);
    return () => document.removeEventListener("keydown", handleViewerKey);
  }, [handleViewerKey]);

  // Touch swipe for mobile
  const swipeRef = useRef({ x: 0, y: 0, dx: 0, dy: 0, moved: false });
  const onTouchStart = (e) => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    swipeRef.current = { x: t.clientX, y: t.clientY, dx: 0, dy: 0, moved: false };
  };
  const onTouchMove = (e) => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    const dx = t.clientX - swipeRef.current.x;
    const dy = t.clientY - swipeRef.current.y;
    swipeRef.current.dx = dx;
    swipeRef.current.dy = dy;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 16) {
      e.preventDefault();
      swipeRef.current.moved = true;
    }
  };
  const onTouchEnd = () => {
    const { dx, dy, moved } = swipeRef.current;
    if (!viewer.open || !Number.isInteger(viewer.idx) || !moved) return;
    const files = filesForIdx(viewer.idx);
    if (!files.length) return;
    const THRESH = 40;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > THRESH) {
      if (dx < 0) {
        setViewer((v) => ({ ...v, at: (v.at + 1) % files.length }));
      } else {
        setViewer((v) => ({ ...v, at: (v.at - 1 + files.length) % files.length }));
      }
    }
    swipeRef.current = { x: 0, y: 0, dx: 0, dy: 0, moved: false };
  };

  // Lock page scroll on mobile when viewer is open
  useEffect(() => {
    if (viewer.open) {
      document.body.classList.add("no-scroll");
      return () => document.body.classList.remove("no-scroll");
    }
  }, [viewer.open]);

  // Keyboard open on collapsed row
  const onVoucherRowKey = (e, idx) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleOpenVouchers(idx, 0);
    }
  };

  return (
    <div className="gs-page min-h-screen bg-slate-950 text-slate-100">
      {/* header */}
      <div className="mx-auto max-w-5xl px-4 pt-6 pb-2">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">🔍 Search Guest</h1>
          <Link to="/" className="shrink-0">
            <button
              type="button"
              className="btn btn-primary bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-4 py-2 text-sm shadow"
            >
              ← Home
            </button>
          </Link>
        </div>
      </div>

      {/* search bar */}
      <div className="mx-auto max-w-5xl px-4 pb-4">
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-3 sm:p-4 shadow-xl">
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Type name or phone…"
              className="flex-1 rounded-xl bg-slate-800/70 border border-slate-700 px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              onClick={doSearch}
              disabled={!canSearch || loading}
              className="btn btn-primary rounded-xl px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white shadow"
            >
              {loading ? "Searching…" : "Search"}
            </button>
          </div>
          {err && <div className="mt-2 text-rose-300 text-sm">{err}</div>}
        </div>
      </div>

      {/* results */}
      <div className="mx-auto max-w-5xl px-4 pb-10">
        {!loading && results.length === 0 && query && (
          <div className="text-slate-400 text-center py-10">No results found.</div>
        )}

        {loading && (
          <div className="grid gap-3 sm:gap-4 grid-cols-1">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-28 rounded-2xl bg-slate-800/50 animate-pulse" />
            ))}
          </div>
        )}

        {!loading && results.length > 0 && (
          <div className="grid grid-cols-1 gap-3 sm:gap-4">
            {results.map((c, idx) => {
              const files = filesForIdx(idx);
              const hasVouchers = files.length > 0;

              return (
                <div
                  key={idx}
                  className="gs-card rounded-2xl border border-slate-800 bg-slate-900/60 p-4 shadow-xl"
                >
                  {editingIndex === idx ? (
                    <div className="space-y-2">
                      <input
                        name="name"
                        value={editForm.name}
                        onChange={handleEditChange}
                        className="w-full rounded-lg bg-slate-800/70 border border-slate-700 px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <input
                        name="phones"
                        value={editForm.phones}
                        onChange={handleEditChange}
                        className="w-full rounded-lg bg-slate-800/70 border border-slate-700 px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <input
                        name="birthday"
                        type="date"
                        value={editForm.birthday || ""}
                        onChange={handleEditChange}
                        className="w-full rounded-lg bg-slate-800/70 border border-slate-700 px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <div className="flex gap-2 pt-1">
                        <button
                          className="btn bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg"
                          onClick={saveEdit}
                        >
                          Save
                        </button>
                        <button
                          className="btn btn-ghost bg-slate-600 hover:bg-slate-700 text-white px-4 py-2 rounded-lg"
                          onClick={cancelEdit}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* vCard tile */}
                      <div className="vcf-card">
                        <div
                          className="vcf-head"
                          onClick={() => toggleExpanded(idx)}
                          role="button"
                          aria-expanded={expanded[idx] ? "true" : "false"}
                          aria-controls={`vcf-body-${idx}`}
                          title="Click to show more"
                        >
                          <div className="vcf-title">
                            <div className="vcf-name">{c.name || "-"}</div>
                            <div className="vcf-sub">📞 {c.phones || "—"}</div>
                          </div>
                          <div className={`vcf-chev ${expanded[idx] ? "rotate" : ""}`}>▶</div>
                        </div>

                        <div
                          id={`vcf-body-${idx}`}
                          className={`vcf-body ${expanded[idx] ? "open" : ""}`}
                        >
                          <div>
                            <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-6 p-3">
                              <div className="flex-1 space-y-1.5">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                  <Field label="📞 Phone" value={c.phones} />
                                  <Field label="📧 Email" value={c.email} />
                                  <Field label="🏢 Company" value={c.company} />
                                  <Field label="🎩 Title" value={c.title} />
                                  <Field label="🎂 Birthday" value={c.birthday} />
                                  <Field label="📍 Address" value={c.address} />
                                </div>
                              </div>

                              <div className="shrink-0 vcf-actions">
                                <button
                                  className="vcf-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    downloadVCard(c);
                                  }}
                                  title="Download .vcf"
                                >
                                  ⬇️ Download vCard
                                </button>
                                <a
                                  className="vcf-btn"
                                  href={`data:text/vcard;charset=utf-8,${encodeURIComponent(buildVCard(c))}`}
                                  download={`${
                                    (c.name || "contact").replace(/[^\w\s.-]+/g, "").trim() || "contact"
                                  }.vcf`}
                                  onClick={(e) => e.stopPropagation()}
                                  title="Save as .vcf"
                                >
                                  💾 Save vCard
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* voucher summary + actions */}
                      <div className="mt-3 flex items-start gap-2 sm:gap-4">
                        <Pill
                          tone={
                            hasVouchers
                              ? "good"
                              : vError(idx)
                              ? "bad"
                              : vLoading(idx)
                              ? "warn"
                              : "default"
                          }
                        >
                          🎟{" "}
                          {vLoading(idx)
                            ? "Checking…"
                            : hasVouchers
                            ? `${files.length} voucher${files.length > 1 ? "s" : ""}`
                            : vError(idx)
                            ? "Error"
                            : "No voucher"}
                        </Pill>

                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => fetchVouchersForIdx(idx)}
                            className="btn btn-ghost rounded-lg px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-60"
                            disabled={vLoading(idx)}
                            title="Refresh vouchers"
                          >
                            Refresh
                          </button>

                          <button
                            onClick={() => startEdit(idx)}
                            className="btn btn-warning rounded-lg px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white"
                          >
                            Edit
                          </button>

                          {/* ✅ DELETE button */}
                          <button
                            onClick={() => deleteGuest(idx)}
                            disabled={dLoading(idx)}
                            className="btn rounded-lg px-3 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white"
                            title="Delete guest"
                          >
                            {dLoading(idx) ? "Deleting…" : "Delete"}
                          </button>
                        </div>
                      </div>

                      {/* Collapsed Voucher Row (opens fullscreen viewer) */}
                      <div className="mt-3 border-t border-slate-800 pt-2">
                        <div
                          role="button"
                          tabIndex={0}
                          aria-label="Open vouchers"
                          onKeyDown={(e) => onVoucherRowKey(e, idx)}
                          onClick={() => handleOpenVouchers(idx, 0)}
                          className={`voucher-collapsed ${hasVouchers ? "" : "disabled"}`}
                          title={hasVouchers ? "Open vouchers" : "No vouchers found"}
                        >
                          <div className="vc-left">
                            <span className="vc-icon">🎟</span>
                            <span className="vc-title">Vouchers</span>
                            <span className="vc-sub">
                              {vLoading(idx)
                                ? "Checking…"
                                : hasVouchers
                                ? `${files.length} file${files.length > 1 ? "s" : ""}`
                                : vError(idx)
                                ? "Error"
                                : "No vouchers"}
                            </span>
                          </div>
                          <div className="vc-right">
                            <span className="vc-hint">{hasVouchers ? "Open" : ""}</span>
                            <span className="vc-chev">▶</span>
                          </div>
                        </div>
                        {vError(idx) && <div className="text-rose-300 text-sm mt-2">{vError(idx)}</div>}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ====== VOUCHER VIEWER MODAL ====== */}
      {viewer.open && Number.isInteger(viewer.idx) && (
        <div className="voucher-dialog" role="dialog" aria-modal="true" onClick={closeViewer}>
          <div className="viewer-card" onClick={(e) => e.stopPropagation()}>
            <div className="viewer-head">
              <div className="viewer-title">🎟 Vouchers</div>
              <button className="viewer-x" onClick={closeViewer}>
                ✕
              </button>
            </div>

            {(() => {
              const files = filesForIdx(viewer.idx);
              const f = files[viewer.at] || {};
              const dlHref = urlForDownload(f);
              const name = f.name || "";

              return (
                <>
                  <div
                    className="viewer-body"
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                  >
                    {viewerBusy ? (
                      <div className="viewer-blank">Loading…</div>
                    ) : viewerErr ? (
                      <div className="viewer-blank">{viewerErr}</div>
                    ) : viewerSrc && isImage(name, viewerMime) ? (
                      <img className="viewer-media" src={viewerSrc} alt={name} />
                    ) : viewerSrc && isPDF(name, viewerMime) ? (
                      <iframe className="viewer-media" src={viewerSrc} title={name} />
                    ) : (
                      <div className="viewer-blank">No preview</div>
                    )}
                  </div>

                  <div className="viewer-foot">
                    <div className="viewer-meta">
                      <div className="viewer-name" title={name}>
                        {name}
                      </div>
                      <div className="viewer-count">
                        {viewer.at + 1} / {files.length}
                      </div>
                    </div>
                    <div className="viewer-actions">
                      <button
                        className="viewer-btn"
                        onClick={() =>
                          setViewer((v) => {
                            const len = files.length || 1;
                            return { ...v, at: (v.at - 1 + len) % len };
                          })
                        }
                        title="Previous (←)"
                      >
                        ← Prev
                      </button>
                      <button
                        className="viewer-btn"
                        onClick={() =>
                          setViewer((v) => {
                            const len = files.length || 1;
                            return { ...v, at: (v.at + 1) % len };
                          })
                        }
                        title="Next (→)"
                      >
                        Next →
                      </button>
                      {dlHref && (
                        <a
                          className="viewer-btn primary"
                          href={dlHref}
                          target="_blank"
                          rel="noreferrer"
                          download
                        >
                          Download
                        </a>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
